import { useEffect, useState } from 'react';
import { Preloader } from 'konsta/react';
import { Lock } from 'lucide-react';
import { clientProfileUpdateSchema, providerEditSchema } from '@harmony/shared';
import { api, type City, type ClientProfile, type ProviderEditForm } from '../api';
import { Field, GenderPicker, LocationField, PhoneField, PhotoGrid, TextInput, inputClass, type LocationValue } from '../components/form';
import { EmptyState, PrimaryButton, TopBar, useToast } from '../components/ui';
import { haptic } from '../telegram';
import { useNav } from '../nav';
import { useErrorText, useT } from '../i18n';

/** First validation problem per field, as translated text. */
function fieldErrors(issues: Array<{ path: Array<string | number>; message: string }>, errText: (e: unknown) => string) {
  const e: Record<string, string> = {};
  for (const i of issues) e[String(i.path[0])] ??= errText(i.message === 'Required' ? 'REQUIRED' : i.message);
  return e;
}

function Saving({ busy, label }: { busy: boolean; label: string }) {
  return <>{busy ? <Preloader className="h-5 w-5" /> : label}</>;
}

// ─────────────── Client ───────────────

export function EditClientPage({ onSaved }: { onSaved?: () => void }) {
  const t = useT();
  const nav = useNav();
  const toast = useToast();
  const errText = useErrorText();
  const [cities, setCities] = useState<City[]>([]);
  const [orig, setOrig] = useState<ClientProfile | null>(null);
  const [name, setName] = useState('');
  const [gender, setGender] = useState<'MALE' | 'FEMALE' | undefined>();
  const [phone, setPhone] = useState('');
  const [loc, setLoc] = useState<LocationValue>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.cities().then(setCities).catch(() => undefined);
    api.clientProfile().then((p) => {
      setOrig(p);
      setName(p.displayName);
      setGender(p.gender);
      setPhone(p.phone);
      setLoc(p.hasGps ? {} : { cityId: p.cityId ?? undefined });
    });
  }, []);

  if (!orig)
    return (
      <div className="h-full">
        <TopBar title={t('edit.title')} />
        <div className="flex justify-center pt-24"><Preloader /></div>
      </div>
    );

  const save = async () => {
    const body: Record<string, unknown> = {};
    if (name.trim() !== orig.displayName) body.displayName = name.trim();
    if (gender && gender !== orig.gender) body.gender = gender;
    if (phone.trim() !== orig.phone) body.phone = phone.trim();
    if (loc.lat !== undefined || (loc.cityId && loc.cityId !== orig.cityId)) body.location = loc;
    const r = clientProfileUpdateSchema.safeParse(body);
    if (!r.success) {
      setErrors(fieldErrors(r.error.issues, errText));
      haptic('error');
      return;
    }
    setBusy(true);
    try {
      if (Object.keys(r.data).length) await api.saveClient(r.data as never);
      haptic('success');
      toast(t('edit.saved'));
      onSaved?.();
      nav.pop();
    } catch (e) {
      setErrors({ submit: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <TopBar title={t('edit.title')} />
      <div className="flex-1 overflow-y-auto px-4 pb-6 pt-4">
        <Field label={t('reg.name')} error={errors.displayName}>
          <TextInput value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('reg.gender')} error={errors.gender}>
          <GenderPicker value={gender} onChange={setGender} />
        </Field>
        <Field label={t('reg.phone')} error={errors.phone}>
          <PhoneField value={phone} onChange={setPhone} />
        </Field>
        <Field label={t('reg.location')} hint={orig.hasGps && loc.lat === undefined && !loc.cityId ? t('reg.location_saved') : undefined} error={errors.location}>
          <LocationField value={loc} onChange={setLoc} cities={cities} />
        </Field>
        {errors.submit && <p className="rounded-xl bg-red-500/10 p-3 text-red-600">{errors.submit}</p>}
      </div>
      <div className="shrink-0 border-t border-black/5 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3">
        <PrimaryButton disabled={busy} onClick={save}>
          <Saving busy={busy} label={t('edit.save')} />
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─────────────── Provider ───────────────

export function EditProviderPage({ onSaved }: { onSaved?: () => void }) {
  const t = useT();
  const nav = useNav();
  const toast = useToast();
  const errText = useErrorText();
  const [cities, setCities] = useState<City[]>([]);
  const [orig, setOrig] = useState<ProviderEditForm | null>(null);
  const [failed, setFailed] = useState(false);
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [phone, setPhone] = useState('');
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<Array<{ key: string; url: string }>>([]);
  const [loc, setLoc] = useState<LocationValue>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.cities().then(setCities).catch(() => undefined);
    api
      .providerEditForm()
      .then((p) => {
        setOrig(p);
        setName(p.displayName);
        setBio(p.bio);
        setPhone(p.phone);
        setPhotos(p.photos);
        setPrices(Object.fromEntries(p.sessionLengths.map((m) => [String(m), p.prices[String(m)] ? String(p.prices[String(m)]) : ''])));
        setLoc(p.hasGps ? {} : { cityId: p.cityId ?? undefined });
      })
      .catch(() => setFailed(true));
  }, []);

  if (!orig)
    return (
      <div className="h-full">
        <TopBar title={t('edit.title')} />
        {failed ? <EmptyState icon="📡" text={t('error.generic')} /> : <div className="flex justify-center pt-24"><Preloader /></div>}
      </div>
    );

  const save = async () => {
    const priceNums = Object.fromEntries(
      Object.entries(prices)
        .filter(([, v]) => v.trim() !== '')
        .map(([k, v]) => [k, Number(v)]),
    );
    const body: Record<string, unknown> = {};
    if (name.trim() !== orig.displayName) body.displayName = name.trim();
    if (bio.trim() !== orig.bio) body.bio = bio.trim();
    if (phone.trim() !== orig.phone) body.phone = phone.trim();
    if (JSON.stringify(priceNums) !== JSON.stringify(orig.prices)) body.prices = priceNums;
    if (photos.map((p) => p.key).join('|') !== orig.photos.map((p) => p.key).join('|')) body.photoKeys = photos.map((p) => p.key);
    if (loc.lat !== undefined || (loc.cityId && loc.cityId !== orig.cityId)) body.location = loc;
    const r = providerEditSchema.safeParse(body);
    if (!r.success) {
      setErrors(fieldErrors(r.error.issues, errText));
      haptic('error');
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      if (Object.keys(r.data).length) await api.saveProvider(r.data);
      haptic('success');
      toast(t('edit.saved'));
      onSaved?.();
      nav.pop();
    } catch (e) {
      setErrors({ submit: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <TopBar title={t('edit.title')} />
      <div className="flex-1 overflow-y-auto px-4 pb-6 pt-4">
        <Field label={t('reg.photos')} hint={t('edit.photos_hint')} error={errors.photoKeys}>
          <PhotoGrid photos={photos} onChange={setPhotos} />
        </Field>
        <Field label={t('reg.name')} error={errors.displayName}>
          <TextInput value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('reg.bio')} hint={`${bio.length}/70`} error={errors.bio}>
          <textarea className={inputClass + ' min-h-[70px] resize-none'} maxLength={70} value={bio} onChange={(e) => setBio(e.target.value)} />
        </Field>
        <Field label={t('provider.prices')} hint={t('reg.price_hint')} error={errors.prices}>
          <div className="grid grid-cols-2 gap-2">
            {orig.sessionLengths.map((m) => (
              <div key={m}>
                <span className="mb-1 block text-sm text-tg-hint">{t('provider.min', { minutes: m })} (ETB)</span>
                <TextInput
                  inputMode="numeric"
                  value={prices[String(m)] ?? ''}
                  onChange={(e) => setPrices((p) => ({ ...p, [String(m)]: e.target.value.replace(/\D/g, '') }))}
                />
              </div>
            ))}
          </div>
        </Field>
        <Field label={t('reg.phone')} error={errors.phone}>
          <PhoneField value={phone} onChange={setPhone} />
        </Field>
        <Field label={t('reg.step_location')} hint={orig.hasGps && loc.lat === undefined && !loc.cityId ? t('reg.location_saved') : undefined} error={errors.location}>
          <LocationField value={loc} onChange={setLoc} cities={cities} />
        </Field>
        <div className="flex items-start gap-2.5 rounded-2xl bg-tg-secondary p-3 text-sm text-tg-hint">
          <Lock size={16} className="mt-0.5 shrink-0" />
          <span>
            {t(`gender.${orig.gender}`)} · {t('provider.age', { age: orig.age })}. {t('edit.locked')}
          </span>
        </div>
        {errors.submit && <p className="mt-3 rounded-xl bg-red-500/10 p-3 text-red-600">{errors.submit}</p>}
      </div>
      <div className="shrink-0 border-t border-black/5 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3">
        <PrimaryButton disabled={busy} onClick={save}>
          <Saving busy={busy} label={t('edit.save')} />
        </PrimaryButton>
      </div>
    </div>
  );
}
